import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

export default function MedicationPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { members } = useAuth();
  const [medications, setMedications] = useState([]);
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [showForm,    setShowForm]    = useState(false);
  const [form, setForm] = useState({
    user_id: '', name: '', dosage: '', times: '', note: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/medications');
      setMedications(data?.medications || []);
      setLogs(data?.logs || []);
    } catch (err) {
      setError('Kunne ikke laste medisiner: ' + (err?.message || 'Ukjent feil'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getTodayStatus = (medId) => {
    const log = logs.find(l => String(l.medication_id) === String(medId));
    return log ? log.status : 'pending';
  };

  const markTaken = async (medId, status) => {
    try {
      await api.post(`/api/medications/${medId}/log`, { status });
      loadData();
    } catch (err) {
      alert('Feil: ' + (err?.message || 'Ukjent feil'));
    }
  };

  const addMed = async (e) => {
    e.preventDefault();
    try {
      const times = form.times
        ? form.times.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await api.post('/api/medications', { ...form, times });
      setForm({ user_id: '', name: '', dosage: '', times: '', note: '' });
      setShowForm(false);
      loadData();
    } catch (err) {
      alert('Feil: ' + (err?.message || 'Ukjent feil'));
    }
  };

  const deleteMed = async (id) => {
    if (!window.confirm('Slett dette medikamentet?')) return;
    try {
      await api.delete(`/api/medications/${id}`);
      loadData();
    } catch (err) {
      alert('Feil: ' + (err?.message || 'Ukjent feil'));
    }
  };

  const inp = {
    width: '100%', padding: '9px 12px', borderRadius: 10,
    border: '1px solid var(--bdr)',
    background: 'var(--input-bg)', color: 'var(--txt)',
    fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box', outline: 'none'
  };
  const lbl = {
    fontSize: 11, fontWeight: 700, color: 'var(--txl)',
    textTransform: 'uppercase', letterSpacing: '.5px',
    display: 'block', marginBottom: 4
  };

  if (loading && !isMobile) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--txl)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>💊</div>
      Laster medisinliste...
    </div>
  );

  if (error) return (
    <div style={{
      padding: 20, background: 'var(--red-bg)', color: 'var(--red)',
      borderRadius: 12, margin: 20, fontSize: 14
    }}>
      {error}
      <br /><br />
      <button onClick={loadData} style={{
        padding: '8px 16px', background: 'var(--red)', color: 'white',
        border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit'
      }}>
        Prøv igjen
      </button>
    </div>
  );

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const glassInp = { padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' };

  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Medisin</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{new Date().toLocaleDateString('nb-NO',{weekday:'long',day:'numeric',month:'long'})}</div>
          </div>
          <button onClick={()=>setShowForm(v=>!v)} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 14px', fontWeight:700, fontSize:14, cursor:'pointer' }}>{showForm?'Avbryt':'+ Ny'}</button>
        </div>
      </div>

      {error && <div style={{ margin:'12px 16px', background:'rgba(247,96,92,0.15)', color:'#F7605C', borderRadius:12, padding:'12px 16px', fontSize:14 }}>{error} <button onClick={loadData} style={{ marginLeft:10, background:'none', border:'none', color:'#F7605C', fontWeight:700, cursor:'pointer', textDecoration:'underline' }}>Prøv igjen</button></div>}

      {showForm && (
        <form onSubmit={addMed} className="glass-card" style={{ margin:'12px 16px', padding:14, display:'flex', flexDirection:'column', gap:10 }}>
          <input style={{...glassInp, width:'100%', boxSizing:'border-box'}} placeholder="Medikament" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} autoFocus required/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <select style={{...glassInp, padding:'11px 12px'}} value={form.user_id} onChange={e=>setForm(f=>({...f,user_id:e.target.value}))} required>
              <option value="">Velg person</option>
              {(members||[]).map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <input style={{...glassInp, padding:'11px 14px'}} placeholder="Dose" value={form.dosage} onChange={e=>setForm(f=>({...f,dosage:e.target.value}))}/>
          </div>
          <input style={{...glassInp, width:'100%', boxSizing:'border-box'}} placeholder="Tidspunkt (Morgen, Kveld)" value={form.times} onChange={e=>setForm(f=>({...f,times:e.target.value}))}/>
          <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Lagre medisin</button>
        </form>
      )}

      <div style={{ padding:'12px 16px 80px' }}>
        {medications.length===0 && !loading && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
            <div style={{ fontSize:48, marginBottom:12 }}>💊</div>
            <div style={{ fontSize:16, fontWeight:700, color:txt }}>Ingen medisiner</div>
          </div>
        )}
        {medications.map(med => {
          const status = getTodayStatus(med.id);
          const member = (members||[]).find(m=>String(m.id)===String(med.user_id));
          let times=[]; try{const p=typeof med.times==='string'?JSON.parse(med.times):med.times;if(Array.isArray(p))times=p;}catch(_){}
          return (
            <div key={med.id} className="glass-card" style={{ marginBottom:8, padding:'14px 16px' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                {member && <div style={{ width:40, height:40, borderRadius:'50%', background:member.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, color:'#fff', flexShrink:0 }}>{member.avatar||(member.name||'?')[0].toUpperCase()}</div>}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:txt, marginBottom:2 }}>{med.name}{med.dosage&&<span style={{ fontSize:12, color:sub, marginLeft:8, background: dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', borderRadius:20, padding:'2px 8px' }}>{med.dosage}</span>}</div>
                  {member && <div style={{ fontSize:12, color:sub, marginBottom:2 }}>{member.name}</div>}
                  {times.length>0 && <div style={{ fontSize:12, color:sub }}>{times.join(' · ')}</div>}
                </div>
                <button onClick={()=>deleteMed(med.id)} style={{ background:'none', border:'none', color:sub, cursor:'pointer', fontSize:18, flexShrink:0 }}>🗑</button>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                {status==='taken' && <div style={{ flex:1, background:'rgba(52,211,153,0.15)', color:'#34D399', borderRadius:10, padding:'9px', textAlign:'center', fontWeight:700, fontSize:13 }}>✓ Tatt i dag</div>}
                {status==='skipped' && <div style={{ flex:1, background:'rgba(251,191,36,0.15)', color:'#FBBF24', borderRadius:10, padding:'9px', textAlign:'center', fontWeight:700, fontSize:13 }}>Hoppet over</div>}
                {status==='pending' && <>
                  <button onClick={()=>markTaken(med.id,'taken')} style={{ flex:1, background:'rgba(52,211,153,0.15)', color:'#34D399', border:'none', borderRadius:10, padding:'9px', fontWeight:700, fontSize:14, cursor:'pointer' }}>✓ Tatt</button>
                  <button onClick={()=>markTaken(med.id,'skipped')} style={{ flex:1, background:'rgba(251,191,36,0.15)', color:'#FBBF24', border:'none', borderRadius:10, padding:'9px', fontWeight:700, fontSize:14, cursor:'pointer' }}>Hopp over</button>
                </>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--txt)', margin: 0 }}>
            Medisin og helse
          </h2>
          <p style={{ fontSize: 13, color: 'var(--txl)', margin: '4px 0 0' }}>
            {new Date().toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          style={{
            padding: '9px 18px', borderRadius: 10, border: 'none',
            background: '#7C6FF7', color: 'white', fontWeight: 700,
            cursor: 'pointer', fontSize: 13, fontFamily: 'inherit'
          }}>
          {showForm ? 'Avbryt' : '+ Legg til medisin'}
        </button>
      </div>

      {/* Legg til skjema */}
      {showForm && (
        <div style={{
          background: 'var(--card)', border: '1px solid var(--bdr)',
          borderRadius: 14, padding: 20, marginBottom: 16
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--txt)', marginBottom: 14, marginTop: 0 }}>
            Nytt medikament
          </h3>
          <form onSubmit={addMed}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Person *</label>
                <select
                  value={form.user_id}
                  onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
                  required style={inp}>
                  <option value="">Velg person</option>
                  {(members || []).map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Medikament *</label>
                <input value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="F.eks. Paracet 500mg" required style={inp} />
              </div>
              <div>
                <label style={lbl}>Dose</label>
                <input value={form.dosage}
                  onChange={e => setForm(f => ({ ...f, dosage: e.target.value }))}
                  placeholder="F.eks. 1 tablett" style={inp} />
              </div>
              <div>
                <label style={lbl}>Tidspunkt (komma-separert)</label>
                <input value={form.times}
                  onChange={e => setForm(f => ({ ...f, times: e.target.value }))}
                  placeholder="F.eks. Morgen, Kveld" style={inp} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Notat</label>
              <input value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="F.eks. Ta med mat" style={inp} />
            </div>
            <button type="submit" style={{
              width: '100%', padding: 11, borderRadius: 10, border: 'none',
              background: '#7C6FF7', color: 'white', fontWeight: 700,
              cursor: 'pointer', fontSize: 14, fontFamily: 'inherit'
            }}>
              Lagre medisin
            </button>
          </form>
        </div>
      )}

      {/* Tom-tilstand */}
      {medications.length === 0 && !showForm && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'var(--card)', borderRadius: 14,
          border: '1px solid var(--bdr)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💊</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--txt)', marginBottom: 6 }}>
            Ingen medisiner registrert
          </div>
          <div style={{ fontSize: 13, color: 'var(--txl)' }}>
            Trykk "+ Legg til medisin" for å komme i gang
          </div>
        </div>
      )}

      {/* Medisinliste */}
      {medications.map(med => {
        const status = getTodayStatus(med.id);
        const member = (members || []).find(m => String(m.id) === String(med.user_id));
        let times = [];
        try {
          const parsed = typeof med.times === 'string' ? JSON.parse(med.times) : med.times;
          if (Array.isArray(parsed)) times = parsed;
        } catch (_) {}

        return (
          <div key={med.id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', background: 'var(--card)',
            borderRadius: 12, border: '1px solid var(--bdr)',
            marginBottom: 8, flexWrap: 'wrap'
          }}>
            {member && (
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: member.color || '#7C6FF7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, color: 'white', flexShrink: 0,
                overflow: 'hidden',
              }}>
                {member.avatar
                  ? <span>{member.avatar}</span>
                  : <span>{(member.name || '?')[0].toUpperCase()}</span>
                }
              </div>
            )}

            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
                  {med.name}
                </span>
                {med.dosage && (
                  <span style={{
                    fontSize: 11, color: 'var(--txl)',
                    background: 'var(--bg2)', padding: '2px 8px', borderRadius: 20
                  }}>
                    {med.dosage}
                  </span>
                )}
              </div>
              {member && (
                <div style={{ fontSize: 12, color: 'var(--txm)', marginBottom: 2 }}>
                  {member.name}
                </div>
              )}
              {times.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--txm)', marginBottom: 2 }}>
                  {times.join(' · ')}
                </div>
              )}
              {med.note && (
                <div style={{ fontSize: 12, color: 'var(--txl)' }}>
                  {med.note}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              {status === 'taken' && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                  background: 'var(--green-bg)', color: 'var(--green)'
                }}>
                  ✓ Tatt i dag
                </span>
              )}
              {status === 'skipped' && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                  background: 'var(--yellow-bg)', color: 'var(--yellow)'
                }}>
                  Hoppet over
                </span>
              )}
              {status === 'pending' && (
                <>
                  <button onClick={() => markTaken(med.id, 'taken')} style={{
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: 'var(--green-bg)', color: 'var(--green)',
                    fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit'
                  }}>
                    Tatt
                  </button>
                  <button onClick={() => markTaken(med.id, 'skipped')} style={{
                    padding: '6px 10px', borderRadius: 8, border: 'none',
                    background: 'var(--yellow-bg)', color: 'var(--yellow)',
                    cursor: 'pointer', fontSize: 12, fontFamily: 'inherit'
                  }}>
                    Hopp
                  </button>
                </>
              )}
              <button onClick={() => deleteMed(med.id)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 16, opacity: 0.4, padding: 4
              }}>
                🗑️
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
